import { useEffect, useState } from "react";
import { Facebook, Instagram, Link2, RefreshCw, Unplug } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const IG_LABELS = {
  views: "Vues", reach: "Portée", accounts_engaged: "Comptes engagés",
  total_interactions: "Interactions totales", likes: "Likes", comments: "Commentaires",
  shares: "Partages", saves: "Enregistrements",
  follows_and_unfollows: "Abonnements / désabonnements", profile_views: "Vues du profil",
};
const FB_LABELS = {
  page_views_total: "Vues de la Page", page_media_view: "Vues de médias",
  page_post_engagements: "Engagements sur les publications", page_fans: "Abonnés (fans)",
  page_fan_adds: "Nouveaux abonnés", page_fan_removes: "Abonnés perdus",
  page_impressions: "Impressions",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR")} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

function StatRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-neutral-100 last:border-0" data-testid={testid}>
      <span className="text-neutral-500">{label}</span>
      <span className="font-semibold">{value === null || value === undefined ? "—" : Number(value).toLocaleString("fr-FR")}</span>
    </div>
  );
}

export function MetaConnect({ clientId }) {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pageId, setPageId] = useState("");
  const [confirmOff, setConfirmOff] = useState(false);
  const [oauthUrl, setOauthUrl] = useState("");

  const load = () => api.get("/meta/status", { params: { client_id: clientId, origin: window.location.origin } })
    .then((r) => setSt(r.data)).catch(() => setSt({ configured: false, connected: false }));

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const meta = sp.get("meta");
    if (meta === "choose_page") toast.success("Autorisation Meta OK — choisissez la Page à associer");
    if (meta === "denied") toast.error("Autorisation Meta refusée");
    if (meta === "error") toast.error("Erreur lors de la connexion Meta");
    if (meta) {
      sp.delete("meta");
      const q = sp.toString();
      window.history.replaceState(null, "", window.location.pathname + (q ? `?${q}` : ""));
    }
    load();
  }, [clientId]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/meta/connect", { params: { client_id: clientId, origin: window.location.origin } });
      if (window.self !== window.top) {
        // App affichée en iframe (preview Emergent intégrée) : Facebook refuse
        // d'être chargé dans une iframe (X-Frame-Options) → nouvel onglet.
        const tab = window.open(r.data.url, "_blank", "noopener");
        if (!tab) setOauthUrl(r.data.url); // popup bloquée → lien manuel affiché
        setBusy(false);
      } else {
        window.location.href = r.data.url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la connexion Meta");
      setBusy(false);
    }
  };

  const selectPage = async () => {
    setBusy(true);
    try {
      await api.post("/meta/select-page", { client_id: clientId, page_id: pageId });
      toast.success("Page Facebook associée au client");
      setPageId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const r = await api.post("/meta/sync", { client_id: clientId });
      const inv = (r.data.invalid_metrics || []).length;
      toast.success(`Statistiques synchronisées${inv ? ` — ${inv} métrique(s) non fournie(s) par Meta` : ""}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de synchronisation");
      load();
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.delete("/meta/connection", { params: { client_id: clientId } });
      toast.success("Compte Meta déconnecté");
      setConfirmOff(false);
      load();
    } catch (e) { toast.error("Erreur"); }
    finally { setBusy(false); }
  };

  if (st === null) {
    return <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="meta-connect"><div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const pending = st.pending_pages || [];
  const stats = st.stats;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-5 mb-6" data-testid="meta-connect">
      <h3 className="font-heading font-bold mb-1 flex items-center gap-2"><Link2 className="w-4 h-4" /> Connexion Meta — Facebook & Instagram</h3>
      <p className="text-xs text-neutral-400 mb-4">API officielle Meta (OAuth) — lecture seule des statistiques. Aucun mot de passe Facebook/Instagram n'est demandé ni enregistré.</p>

      {!st.configured && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2" data-testid="meta-not-configured">
          <p className="font-medium text-amber-900">Intégration Meta non configurée</p>
          <p className="text-xs text-amber-800">Renseignez META_APP_ID et META_APP_SECRET dans les Secrets du projet, puis ajoutez cette URL de redirection dans Meta for Developers :</p>
          <code className="block text-[11px] bg-white border border-amber-200 rounded px-2 py-1.5 break-all" data-testid="meta-redirect-uri">{st.redirect_uri}</code>
        </div>
      )}

      {st.configured && !st.connected && pending.length === 0 && (
        <div className="space-y-3" data-testid="meta-not-connected">
          <p className="text-sm text-neutral-500">Compte Meta non connecté</p>
          <Button size="sm" onClick={connect} disabled={busy} className="bg-[#1877F2] hover:bg-[#1664d8] text-white gap-1.5" data-testid="meta-connect-btn">
            <Facebook className="w-3.5 h-3.5" /> {busy ? "Redirection…" : "Connecter Facebook & Instagram"}
          </Button>
          {oauthUrl && (
            <p className="text-xs text-neutral-500">
              L'onglet a été bloqué par le navigateur :{" "}
              <a href={oauthUrl} target="_blank" rel="noreferrer" className="font-medium text-blue-600 hover:underline" data-testid="meta-connect-manual-link">
                ouvrir l'autorisation Meta dans un nouvel onglet
              </a>
            </p>
          )}
        </div>
      )}

      {!st.connected && pending.length > 0 && (
        <div className="space-y-3" data-testid="meta-page-picker">
          <p className="text-sm font-medium">Plusieurs Pages disponibles — choisissez celle à associer à ce client :</p>
          <div className="space-y-1.5">
            {pending.map((p) => (
              <label key={p.id} className={`flex items-center gap-2.5 border rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors ${pageId === p.id ? "border-neutral-900 bg-neutral-50" : "border-neutral-200 hover:border-neutral-400"}`} data-testid={`meta-page-option-${p.id}`}>
                <input type="radio" name="meta-page" checked={pageId === p.id} onChange={() => setPageId(p.id)} />
                <Facebook className="w-4 h-4 text-blue-600 shrink-0" />
                <span className="font-medium">{p.name}</span>
                {p.has_instagram
                  ? <span className="text-[11px] text-pink-600 flex items-center gap-1"><Instagram className="w-3 h-3" /> Instagram lié</span>
                  : <span className="text-[11px] text-neutral-400">aucun Instagram lié</span>}
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={selectPage} disabled={!pageId || busy} className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="meta-select-page-btn">
              {busy ? "Association…" : "Associer cette Page au client"}
            </Button>
            <button onClick={connect} className="text-xs text-neutral-500 hover:text-neutral-900 underline" data-testid="meta-restart-oauth">Recommencer l'autorisation</button>
          </div>
        </div>
      )}

      {st.connected && (
        <div className="space-y-4" data-testid="meta-connected">
          <div className="space-y-1.5">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Meta</p>
            <div className="flex items-center gap-2.5 text-sm" data-testid="meta-fb-status">
              <Facebook className="w-4 h-4 text-blue-600" />
              <span className="font-medium">{st.facebook_page_name}</span>
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-medium rounded-full px-2.5 py-0.5 border ${st.connection_status === "connected" ? "text-green-700 bg-green-50 border-green-200" : "text-red-700 bg-red-50 border-red-200"}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.connection_status === "connected" ? "bg-green-500" : "bg-red-500"}`} />
                {st.connection_status === "connected" ? "Connecté" : "Reconnexion requise"}
              </span>
            </div>
            <div className="flex items-center gap-2.5 text-sm" data-testid="meta-ig-status">
              <Instagram className="w-4 h-4 text-pink-500" />
              {st.instagram_username ? (
                <>
                  <span className="font-medium">@{st.instagram_username}</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Connecté
                  </span>
                </>
              ) : (
                <span className="text-neutral-400 text-xs">Aucun compte Instagram professionnel lié à cette Page</span>
              )}
            </div>
            <p className="text-xs text-neutral-400" data-testid="meta-last-sync">Dernière synchronisation : {fmtDate(st.last_sync_at)}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <Button size="sm" onClick={sync} disabled={busy} className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5" data-testid="meta-sync-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "Synchronisation…" : "Synchroniser maintenant"}
            </Button>
            <Button size="sm" variant="outline" onClick={connect} disabled={busy} data-testid="meta-reconnect-btn">Reconnecter Meta</Button>
            {confirmOff ? (
              <span className="flex items-center gap-2 text-xs">
                Confirmer la déconnexion ?
                <button onClick={disconnect} className="font-semibold text-red-600 hover:underline" data-testid="meta-disconnect-confirm">Oui, déconnecter</button>
                <button onClick={() => setConfirmOff(false)} className="text-neutral-500 hover:underline" data-testid="meta-disconnect-cancel">Annuler</button>
              </span>
            ) : (
              <Button size="sm" variant="outline" onClick={() => setConfirmOff(true)} className="text-red-600 border-red-200 hover:bg-red-50 gap-1.5" data-testid="meta-disconnect-btn">
                <Unplug className="w-3.5 h-3.5" /> Déconnecter
              </Button>
            )}
          </div>

          {stats ? (
            <div className="border border-neutral-200 rounded-xl p-4 space-y-4 bg-neutral-50/50" data-testid="meta-stats">
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Test — Statistiques récupérées depuis Meta</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="meta-stats-ig">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Instagram className="w-4 h-4 text-pink-500" /> Instagram {stats.instagram?.username ? `— @${stats.instagram.username}` : ""}</p>
                  {stats.instagram ? (
                    <>
                      <StatRow label="Abonnés" value={stats.instagram.followers_count} testid="meta-ig-followers" />
                      <StatRow label="Publications" value={stats.instagram.media_count} testid="meta-ig-media-count" />
                      {Object.entries(IG_LABELS).map(([k, label]) => (
                        <StatRow key={k} label={label} value={(stats.instagram.insights || {})[k]} testid={`meta-ig-${k}`} />
                      ))}
                    </>
                  ) : <p className="text-xs text-neutral-400">Aucun compte Instagram lié.</p>}
                </div>
                <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="meta-stats-fb">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Facebook className="w-4 h-4 text-blue-600" /> Facebook {stats.facebook?.page_name ? `— ${stats.facebook.page_name}` : ""}</p>
                  <StatRow label="Abonnés / followers" value={stats.facebook?.followers_count ?? stats.facebook?.fan_count} testid="meta-fb-followers" />
                  {Object.entries(FB_LABELS).map(([k, label]) => (
                    <StatRow key={k} label={label} value={(stats.facebook?.insights || {})[k]} testid={`meta-fb-${k}`} />
                  ))}
                </div>
              </div>
              {(stats.invalid_metrics || []).length > 0 && (
                <p className="text-[11px] text-neutral-400" data-testid="meta-invalid-metrics">
                  Métriques non fournies par Meta (retirées ou indisponibles) : {stats.invalid_metrics.join(", ")}
                </p>
              )}
              {(stats.media || []).length > 0 && (
                <div className="bg-white border border-neutral-200 rounded-lg p-4" data-testid="meta-media-table">
                  <p className="text-sm font-semibold mb-2">Médias Instagram récents — performance</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-neutral-400 border-b border-neutral-100">
                          <th className="py-1.5 pr-3 font-medium">Date</th>
                          <th className="py-1.5 pr-3 font-medium">Type</th>
                          <th className="py-1.5 pr-3 font-medium">Légende</th>
                          <th className="py-1.5 pr-3 font-medium text-right">Vues</th>
                          <th className="py-1.5 pr-3 font-medium text-right">Likes</th>
                          <th className="py-1.5 pr-3 font-medium text-right">Com.</th>
                          <th className="py-1.5 pr-3 font-medium text-right">Partages</th>
                          <th className="py-1.5 pr-3 font-medium text-right">Enreg.</th>
                          <th className="py-1.5 font-medium text-right">Interactions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.media.map((m) => (
                          <tr key={m.media_id} className="border-b border-neutral-50 last:border-0" data-testid={`meta-media-${m.media_id}`}>
                            <td className="py-1.5 pr-3 whitespace-nowrap">{m.timestamp ? new Date(m.timestamp).toLocaleDateString("fr-FR") : "—"}</td>
                            <td className="py-1.5 pr-3">{m.media_type || "—"}</td>
                            <td className="py-1.5 pr-3 max-w-40 truncate">
                              {m.permalink ? <a href={m.permalink} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{m.caption || "Voir"}</a> : (m.caption || "—")}
                            </td>
                            <td className="py-1.5 pr-3 text-right">{m.metrics.views != null ? m.metrics.views.toLocaleString("fr-FR") : "—"}</td>
                            <td className="py-1.5 pr-3 text-right">{(m.metrics.likes ?? m.like_count) != null ? (m.metrics.likes ?? m.like_count).toLocaleString("fr-FR") : "—"}</td>
                            <td className="py-1.5 pr-3 text-right">{(m.metrics.comments ?? m.comments_count) != null ? (m.metrics.comments ?? m.comments_count).toLocaleString("fr-FR") : "—"}</td>
                            <td className="py-1.5 pr-3 text-right">{m.metrics.shares != null ? m.metrics.shares.toLocaleString("fr-FR") : "—"}</td>
                            <td className="py-1.5 pr-3 text-right">{m.metrics.saved != null ? m.metrics.saved.toLocaleString("fr-FR") : "—"}</td>
                            <td className="py-1.5 text-right font-semibold">{m.metrics.total_interactions != null ? m.metrics.total_interactions.toLocaleString("fr-FR") : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-neutral-400 bg-neutral-50 border border-dashed border-neutral-300 rounded-lg p-4" data-testid="meta-no-stats">
              Aucune statistique pour le moment — lancez une première synchronisation.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
